import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { CSVLink } from "react-csv"
import { Container, Box, Grid, Link, Typography, Divider, Card, Button, Snackbar, Alert } from '@mui/material'
import { connect } from 'react-redux'
import parse from 'html-react-parser'
import {
  MakeAmountStyle,
  dealPriceFormValidation,
  stakeAmountFormValidation,
  makeDealCSVData,
  dealFormValidation,
  makeOfferPeriod
} from '@utils/dataHelper'
import {
  getDealInfo,
  getAdminAddress,
  unStake,
  overUnStake,
  setOfferInfo,
  claimDeal,
  addStake,
  checkAllowance,
  approveToken,
  updateDeal,
  modifyDeal,
  getEstimatedBonus
} from '@hooks/contract'
import {
  setDealCrypto,
  setOfferPeriod
} from '@modules/redux/actions'
import { EmojiFlags, DownloadForOffline } from '@mui/icons-material'
import StatusBadge from '@components/StatusBadge'
import ProgressBar from '@components/ProgressBar'
import MyStakeItem from '@components/MyStakeItem'
import SetPriceDialog from '@components/Dialogs/SetPriceDialog'
import AddStakeDialog from '@components/Dialogs/AddStakeDialog'
import ClaimDealDialog from '@components/Dialogs/ClaimDealDialog'
import UpdateDealDialog from '@components/Dialogs/UpdateDealDialog'
import AddDealDialog from '@components/Dialogs/AddDealDialog'
import ApprovalDialog from '@components/Dialogs/ApprovalDialog'
import ClockLoader from '@components/Loaders/Clock'
import { TOKENS_INFO } from '@utils/tokenHelper'
import { useWallet } from '@utils/walletHelper'
import './DealInfo.scss'
import Layout from "../Layout"

const DealInfo = ({
  dealPrice,
  offerTerms,
  stakeAmount,
  dealData,
  setDealCrypto,
  setOfferPeriod
}) => {
  const { connected, connecting, walletAddress } = useWallet()
  const { id } = useParams()
  const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
  const [dealInfo, setDealInfo] = useState(null)
  const [textFieldError, setTextFieldError] = useState({});
  const [showDealPriceDialog, setShowDealPriceDialog] = useState(false)
  const [showAddStakeDialog, setAddStakeDialog] = useState(false)
  const [showUpdateDealDialog, setUpdateDealDialog] = useState(false)
  const [showClaimDealDialog, setClaimDealDialog] = useState(false)
  const [showModifyDealDialog, setModifyDealDialog] = useState(false)
  const [dealTokenInfo, setDealTokenInfo] = useState([])
  const [dealStatus, setDealStatus] = useState(0)
  const [decimal, setDecimal] = useState(0)
  const [showAddStake, setShowAddStake] = useState(false)
  const [showLeadInvestor, setShowLeadInvestor] = useState(false)
  const [showUpdateDeal, setShowUpdateDeal] = useState(false)
  const [isApproved, setIsApproved] = useState(false)
  const [isPending, setIsPending] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [loader, setLoader] = useState(false)
  const [updateDealStatus, setUpdateDealStatus] = useState(0)
  const [dealCSVData, setDealCSVData] = useState([])
  const [nextBonus, setNextBonus] = useState(0)

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: undefined,
  })

  useEffect(() => {
    let isMounted = true
    const fetchDataHandler = async () => {
      if (!connecting) {
        setLoader(true)
        try {
          const data = await getDealInfo(connected, walletAddress, id)
          if (isMounted) {
            if (data) {
              setDealCSVData(makeDealCSVData(data.stakes, data.presaleAmount, data.maxsaleAmount, data.dealPrice))
              setDealInfo(data)
            }
          }
        } catch (error) {
          console.log(error)
        }
        setLoader(false)
      }
    }

    fetchDataHandler()

    return () => { isMounted = false }
  }, [walletAddress, connected, connecting, id])

  useMemo(() => {
    const checkIsAdmin = async () => {
      const adminAddress = await getAdminAddress()
      setIsAdmin(adminAddress.toLowerCase() === walletAddress.toLowerCase())
    }

    checkIsAdmin()
  }, [walletAddress])

  // useEffect(() => {
  //   let _interval = setInterval(() => {
  //     fetchData()
  //   }, 10000)

  //   return () => {
  //     clearInterval(_interval)
  //   }
  // })

  const fetchData = async () => {
    try {
      const data = await getDealInfo(connected, walletAddress, id)
      setDealCSVData(makeDealCSVData(data.stakes, data.presaleAmount, data.maxsaleAmount, data.dealPrice))
      setDealInfo(data)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    const fetchAllowance = async () => {
      if (dealTokenInfo.length > 0) {
        const _isApproved = await checkAllowance(connected, walletAddress, dealTokenInfo[0])
        setIsApproved(_isApproved)

        // Get next bonus for staking
        setNextBonus(await getEstimatedBonus(id, 0, dealTokenInfo[0]))
      }
    }
    fetchAllowance()
  }, [dealTokenInfo, connected, walletAddress, id])

  useEffect(() => {
    if (dealInfo !== null) {
      setDealStatus(dealInfo.status)
      const _tokenInfo = TOKENS_INFO.filter((token) => {
        return token.address.toLowerCase() === dealInfo.dealToken.toLowerCase();
      });
      setDealTokenInfo(_tokenInfo)
      const _decimal = parseInt(_tokenInfo[0].decimal)
      setDecimal(_decimal)
      setDealCrypto(dealInfo.dealToken)
      setOfferPeriod(dealInfo.offerPeriod)
    }
  }, [dealInfo])

  useEffect(() => {
    if (dealInfo !== null) {
      if (dealStatus < 2) {
        if (dealInfo.leadInvestor !== ZERO_ADDRESS && dealInfo.stakeCount === 0) {
          setShowAddStake(walletAddress.toLowerCase() === dealInfo.leadInvestor.toLowerCase())
        } else {
          setShowAddStake(true)
        }
      } else if (dealStatus < 3) {
        setShowAddStake(true)
      } else {
        setShowAddStake(false)
      }

      if (dealStatus < 2) {
        setShowUpdateDeal(walletAddress.toLowerCase() === dealInfo.sponsor.toLowerCase())
      } else {
        setShowUpdateDeal(false)
      }
    }
  }, [dealStatus, dealInfo, walletAddress])

  useEffect(() => {
    if (!showAddStake && dealStatus < 2) {
      setShowLeadInvestor(true)
    } else {
      setShowLeadInvestor(false)
    }
  }, [showAddStake, dealStatus])

  const unStakeHandler = async (stakeId) => {
    setLoader(true)
    let result = await unStake(connected, walletAddress, stakeId)
    if (result) {
      await fetchData()
      displayNotify('success', 'Your transaction has succeeded.')
    } else {
      displayNotify('error', 'Your transaction has failed. Please try again!')
    }
    setLoader(false)
  }

  const overUnStakeHandler = async (stakeId) => {
    setLoader(true)
    let result = await overUnStake(connected, walletAddress, stakeId)
    if (result) {
      await fetchData()
      displayNotify('success', 'Your transaction has succeeded.')
    } else {
      displayNotify('error', 'Your transaction has failed. Please try again!')
    }
    setLoader(false)
  }

  const setOfferInfoHandler = async () => {
    const validCheck = dealPriceFormValidation(dealPrice, setTextFieldError)
    if (!validCheck) {
      setShowDealPriceDialog(false)
      setLoader(true)
      let result = await setOfferInfo(connected, walletAddress, id, dealPrice, offerTerms)
      if (result) {
        await fetchData()
        displayNotify('success', 'Your transaction has succeeded.')
      } else {
        displayNotify('error', 'Your transaction has failed. Please try again!')
      }
      setLoader(false)
    }
  }

  const claimDealHandler = async () => {
    setClaimDealDialog(false)
    setLoader(true)
    let result = await claimDeal(connected, walletAddress, id)
    if (result) {
      await fetchData()
      displayNotify('success', 'Your transaction has succeeded.')
    } else {
      displayNotify('error', 'Your transaction has failed. Please try again!')
    }
    setLoader(false)
  }

  const updateDealHandler = async () => {
    setUpdateDealDialog(false)
    setLoader(true)
    let result = await updateDeal(connected, walletAddress, id, updateDealStatus)
    if (result) {
      await fetchData()
      displayNotify('success', 'Your transaction has succeeded.')
    } else {
      displayNotify('error', 'Your transaction has failed. Please try again!')
    }
    setLoader(false)
  }

  const addStakeHandler = async () => {
    const minStakeAmount = dealInfo.minStakeAmount / (10 ** decimal)
    const maxStakeAmount = dealInfo.maxStakeAmount / (10 ** decimal)
    const validCheck = stakeAmountFormValidation(stakeAmount, minStakeAmount, maxStakeAmount, setTextFieldError)
    if (!validCheck) {
      setAddStakeDialog(false)
      setLoader(true)
      let amount = stakeAmount * (10 ** decimal)
      let result = await addStake(connected, walletAddress, id, amount)
      displayNotify(result[0], result[1])
      await fetchData()
      setLoader(false)
    }
  }

  const approveHandler = async () => {
    setIsPending(true)
    const result = await approveToken(connected, walletAddress, dealTokenInfo[0])
    if (result) {
      displayNotify('success', 'Your transaction has succeeded.')
      setIsApproved(true)
    } else {
      displayNotify('error', 'Your transaction has failed. Please try again!')
    }
    setIsPending(false)
  }

  const modifyDealHandler = async () => {
    const validCheck = dealFormValidation(dealData, setTextFieldError, "edit")
    if (!validCheck) {
      setModifyDealDialog(false)
      setLoader(true)
      let result = await modifyDeal(connected, walletAddress, id, dealData, dealInfo.isLeadStaked)
      if (result) {
        await fetchData()
        displayNotify('success', 'Your transaction has succeeded.')
      } else {
        displayNotify('error', 'Your transaction has failed. Please try again!')
      }
      setLoader(false)
    }
  }

  const closeModal = () => {
    setShowDealPriceDialog(false)
    setAddStakeDialog(false)
    setUpdateDealDialog(false)
    setModifyDealDialog(false)
    setClaimDealDialog(false)
  }

  const displayNotify = (type, content) => {
    setAlert({
      open: true,
      message: content,
      severity: type,
    })
  }

  return (
    <Layout>
      <Container fixed className="dealInfo" sx={{ mt: 5 }}>
        {dealInfo === null ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center", paddingTop: "15vh" }}
          >
            <Box>There is no data</Box>
          </Box>
        ) : (
          <Grid container spacing={10}>
            <Grid
              item
              sm={12}
              md={6}
              lg={6}
            >
              <Box>
                <Box
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}
                >
                  <Box>
                    <StatusBadge status={dealStatus} hoursLeft={dealInfo.hoursLeft} />
                  </Box>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  >
                    {
                      dealInfo.sponsor.toLowerCase() === walletAddress.toLowerCase() && (
                        <Box>
                          {dealStatus === 2 && dealInfo.hoursLeft === 0 && (
                            <Button
                              className="btn-yellow btn-sm"
                              sx={{ mr: 2 }}
                              onClick={() => { setUpdateDealDialog(true); setUpdateDealStatus(3); }}
                            >
                              Set Delivering
                            </Button>
                          )}
                          {dealStatus === 1 && (
                            <Button
                              className="btn-yellow btn-sm"
                              sx={{ mr: 2 }}
                              onClick={() => setShowDealPriceDialog(true)}
                            >
                              Set Offer
                            </Button>
                          )}
                          {dealStatus === 4 && (
                            <Button
                              className="btn-default btn-sm"
                              sx={{ mr: 2 }}
                              onClick={() => setClaimDealDialog(true)}
                            >
                              Claim Deal
                            </Button>
                          )}
                          {showUpdateDeal && (
                            <Button
                              className="btn-info btn-sm"
                              sx={{ mr: 2 }}
                              onClick={() => { setModifyDealDialog(true) }}
                            >
                              Update Deal
                            </Button>
                          )}
                          {dealStatus !== 5 && (
                            <Button
                              className="btn-info btn-sm"
                              onClick={() => { setUpdateDealDialog(true); setUpdateDealStatus(5); }}
                            >
                              Close Deal
                            </Button>
                          )}
                        </Box>
                      )
                    }
                    {dealStatus === 3 && isAdmin && (
                      <Button
                        className="btn-info btn-sm"
                        onClick={() => { setUpdateDealDialog(true); setUpdateDealStatus(4); }}
                      >
                        Set Claiming
                      </Button>
                    )}
                  </Box>
                </Box>
                <Divider />
                <Box
                  sx={{ display: "flex", alignItems: "top", justifyContent: "space-between", mt: 3 }}
                >
                  <Box sx={{ mr: 2 }}>
                    <Typography
                      className="font-40 font-bold text-black"
                      sx={{ lineHeight: 1.2 }}
                    >
                      {dealInfo.name}
                    </Typography>
                    {
                      dealStatus > 1 && (
                        <>
                          <Box
                            sx={{ mt: 2 }}
                          >
                            <Typography
                              variant="h2"
                              className="font-bold font-24"
                            >
                              Offer Terms
                            </Typography>
                            <Box
                              sx={{ pt: 1 }}
                            >
                              <Typography
                                variant="span"
                                className="font-bold"
                              >
                                Deal Price:
                              </Typography>
                              <Typography
                                variant="span"
                                className="text-blue-dark font-bold"
                                sx={{ pl: 1, mt: 1 }}
                              >
                                ${MakeAmountStyle(dealInfo.dealPrice)}
                              </Typography>
                              <Box>
                                {parse(dealInfo.offerTerms)}
                              </Box>
                            </Box>
                          </Box>
                        </>
                      )
                    }
                  </Box>
                  <Box className="deal-logo text-right" sx={{ mt: 1 }}>
                    <img src={dealInfo.logo} alt="deal logo" />
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  className="font-bold font-24"
                  sx={{ mb: 1, mt: 4 }}
                >
                  Description
                </Typography>
                <Box
                  className="text-black"
                >
                  {parse(dealInfo.desc)}
                </Box>
                {dealInfo.url !== '' && (
                  <Box
                    sx={{ mt: 1 }}
                  >
                    <Link href={dealInfo.url.toLowerCase().includes('http') ? dealInfo.url : "//" + dealInfo.url} underline='none' target="_blank" sx={{ color: '#006EAA' }}>
                      {dealInfo.url}
                    </Link>
                  </Box>
                )}
                <Card
                  variant='outlined'
                  sx={{ mt: 3, py: 2, px: 3, mx: "auto", borderRadius: "10px" }}
                >
                  <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        ${MakeAmountStyle(dealInfo.presaleAmount, true)}
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Presail Amount
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        {makeOfferPeriod(dealInfo.offerPeriod)}
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Offer Period
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        ${MakeAmountStyle(dealInfo.minsaleAmount, true)}
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Minimum Sale Amount
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        ${MakeAmountStyle(dealInfo.maxsaleAmount, true)}
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Maximum Sale Amount
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        {dealInfo.startBonus}%
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Start Bonus
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        {dealInfo.endBonus}%
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        End Bonus
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 2, mb: 1 }} />
                  <Grid container spacing={1} sx={{ textAlign: 'center' }}>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        {dealInfo.minStakeAmount === 0 ? '-' : `$${MakeAmountStyle(dealInfo.minStakeAmount, true)}`}
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Minimum Stake Amount
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                    >
                      <Typography
                        className="text-blue-dark font-bold"
                      >
                        {dealInfo.maxStakeAmount === 0 ? '-' : `$${MakeAmountStyle(dealInfo.maxStakeAmount, true)}`}
                      </Typography>
                      <Typography
                        className="font-sm font-bold"
                      >
                        Maximum Stake Amount
                      </Typography>
                    </Grid>
                  </Grid>
                </Card>
              </Box>
            </Grid>
            <Grid
              item
              sm={12}
              md={6}
              lg={6}
            >
              <Divider sx={{ pt: 1, mb: 3, mt: '32px' }} />
              <ProgressBar
                preSale={dealInfo.presaleAmount}
                stakes={dealInfo.stakeCount}
                staked={dealInfo.stakeAmount}
                padding="sm"
              />
              {connected && walletAddress !== '' && dealStatus < 4 && (
                <>
                  <Card
                    variant='outlined'
                    className="card-my-position"
                    sx={{ my: 3 }}
                  >
                    {showLeadInvestor ? (
                      <Typography className="font-bold"
                      >
                        Waiting for stake by lead investor: {dealInfo.leadInvestor}
                      </Typography>
                    ) : (
                      <>
                        <Box
                          className="text-center text-gray"
                        >
                          <Typography
                            className="font-24 font-bold"
                          >
                            {dealInfo.stakeCount ? dealInfo.stakeCount + 1 : 1}
                          </Typography>
                          <Typography
                            className="font-sm font-bold"
                          >
                            NEXT POSITION
                          </Typography>
                        </Box>
                        {nextBonus > 0 && (
                          <Box
                            className="text-center text-gray"
                          >
                            <Typography
                              className="font-24 font-bold"
                            >
                              {Number(nextBonus)} %
                            </Typography>
                            <Typography
                              className="font-sm font-bold"
                            >
                              NEXT BONUS
                            </Typography>
                          </Box>
                        )}
                        {showAddStake && (
                          <Button
                            className="btn-info"
                            startIcon={<EmojiFlags />}
                            onClick={() => setAddStakeDialog(true)}
                          >
                            Add Stake
                          </Button>
                        )}
                      </>
                    )}
                  </Card>
                </>
              )}
              {
                connected && dealInfo.myStakes.length > 0 && (
                  <>
                    <Divider sx={{ mt: 2, mb: 3 }} />
                    <Typography
                      variant="h2"
                      className="font-bold font-28 text-center"
                      sx={{ mb: 2 }}
                    >
                      My Stakes
                    </Typography>
                    <Box>
                      {
                        dealInfo.myStakes.map((stake, index) => {
                          return (
                            <MyStakeItem
                              key={index}
                              stakeInfo={stake}
                              presaleAmount={dealInfo.presaleAmount}
                              maxsaleAmount={dealInfo.maxsaleAmount}
                              status={dealStatus}
                              isUnstaking={dealStatus !== 3 && dealStatus !== 4}
                              unStakeHandler={() => unStakeHandler(stake.stakeId)}
                              overUnStakeHandler={() => overUnStakeHandler(stake.stakeId)}
                              isOwn={stake.staker.toLowerCase() === walletAddress.toLowerCase()}
                              disableUnstakeBtn={false}
                            />
                          )
                        })
                      }
                    </Box>
                  </>
                )
              }
              {
                dealInfo.stakes.length > 0 && (
                  <>
                    <Divider sx={{ mt: 3, mb: 3 }} />
                    <Box
                      sx={{ display: "flex", justifyContent: "center", position: "relative", mb: 2 }}
                    >
                      <Typography
                        variant="h3"
                        className="font-28 text-center font-bold"
                      >
                        All Stakes
                      </Typography>
                      {dealCSVData.length > 0 && dealInfo.sponsor.toLowerCase() === walletAddress.toLowerCase() && dealStatus > 2 && dealStatus < 5 && (
                        <Box
                          sx={{ position: "absolute", right: 0, top: 0 }}
                        >
                          <Button
                            className="btn-info btn-sm"
                            startIcon={<DownloadForOffline />}
                          >
                            <CSVLink
                              data={dealCSVData}
                              filename={`Presail_${dealInfo.name}.csv`}
                              className="text-white no-underline"
                            >
                              Download CSV
                            </CSVLink>
                          </Button>
                        </Box>
                      )}
                    </Box>
                    {
                      dealInfo.stakes.map((stake, index) => {
                        return (
                          <MyStakeItem
                            key={index}
                            stakeInfo={stake}
                            presaleAmount={dealInfo.presaleAmount}
                            maxsaleAmount={dealInfo.maxsaleAmount}
                            status={dealStatus}
                            isUnstaking={dealStatus !== 3 && dealStatus !== 4}
                            unStakeHandler={() => unStakeHandler(stake.stakeId)}
                            overUnStakeHandler={() => overUnStakeHandler(stake.stakeId)}
                            isOwn={stake.staker.toLowerCase() === walletAddress.toLowerCase()}
                            disableUnstakeBtn={true}
                          />
                        )
                      })
                    }
                  </>
                )
              }
            </Grid>
          </Grid>
        )}
        {showDealPriceDialog &&
          <SetPriceDialog
            open={showDealPriceDialog}
            closeModal={closeModal}
            textFieldError={textFieldError}
            setPriceHandler={setOfferInfoHandler}
          />
        }
        {showAddStakeDialog && isApproved &&
          <AddStakeDialog
            open={showAddStakeDialog}
            closeModal={closeModal}
            dealId={id}
            dealInfo={dealInfo}
            dealTokenInfo={dealTokenInfo[0]}
            isPending={isPending}
            addStakeHandler={addStakeHandler}
            textFieldError={textFieldError}
            decimal={dealTokenInfo.length > 0 ? parseInt(dealTokenInfo[0].decimal) : 18}
          />
        }
        {!isApproved &&
          <ApprovalDialog
            open={showAddStakeDialog}
            closeModal={closeModal}
            isPending={isPending}
            approveHandler={approveHandler}
          />
        }
        {showClaimDealDialog &&
          <ClaimDealDialog
            open={showClaimDealDialog}
            closeModal={closeModal}
            claimDealHandler={claimDealHandler}
          />
        }
        {showUpdateDealDialog &&
          <UpdateDealDialog
            open={showUpdateDealDialog}
            closeModal={closeModal}
            updateDealHandler={updateDealHandler}
            isEnough={dealInfo.stakeAmount >= dealInfo.minsaleAmount}
            status={updateDealStatus}
          />
        }
        {showModifyDealDialog &&
          <AddDealDialog
            open={showModifyDealDialog}
            closeModal={closeModal}
            textFieldError={textFieldError}
            eventHandler={modifyDealHandler}
            decimal={decimal}
            dealInfo={dealInfo}
            isNew={false}
          />
        }
        {loader && <ClockLoader />}
        <Snackbar
          open={alert.open}
          autoHideDuration={6000}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          <Alert
            onClose={() => setAlert({ ...alert, open: false })}
            severity={alert.severity}
          >
            {alert.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  )
}

const mapStateToProps = ({ deal, stake }) => ({
  dealPrice: deal.price,
  offerTerms: deal.offerTerms,
  stakeAmount: stake.amount,
  dealData: deal
});

const mapToDispatchProps = (dispatch) => ({
  setDealCrypto: (crypto) => dispatch(setDealCrypto(crypto)),
  setOfferPeriod: (period) => dispatch(setOfferPeriod(period))
});

export default connect(mapStateToProps, mapToDispatchProps)(DealInfo);